import React, { useState, useCallback } from 'react';
import { Plus, Upload, FileText, FileSpreadsheet, X, Check, AlertCircle, Trash2 } from 'lucide-react';
import VotingPanel from '../VotingPanel';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGResolutionsTab = ({ selectedAG, userRole, token, onNewResolution, onVoteComplete, hasConfirmedParticipation, participationMode, onResolutionsImported }) => {
  const [dragActive, setDragActive] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleImport = useCallback(async (file) => {
    if (!file) return;
    
    const filename = file.name.toLowerCase();
    if (!filename.endsWith('.docx') && !filename.endsWith('.csv')) {
      setError('Format non supporté. Utilisez un fichier Word (.docx) ou CSV (.csv)');
      return;
    }
    
    setImporting(true);
    setError(null);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/assemblees/${selectedAG.id}/resolutions/import`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setImportResult({
        success: true,
        message: response.data.message,
        count: response.data.resolutions?.length || 0
      });
      
      // Refresh resolutions list
      if (onResolutionsImported) {
        onResolutionsImported();
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => setImportResult(null), 5000);
      
    } catch (err) {
      console.error('Error importing resolutions:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  }, [selectedAG?.id, token, onResolutionsImported]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleImport(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleImport(e.target.files[0]);
    }
  };

  const handleDeleteAllResolutions = async () => {
    setDeleting(true);
    setError(null);
    
    try {
      await axios.delete(
        `${API}/assemblees/${selectedAG.id}/resolutions/all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setImportResult({
        success: true,
        message: 'Toutes les résolutions ont été supprimées'
      });
      
      setShowDeleteConfirm(false);
      
      // Refresh resolutions list
      if (onResolutionsImported) {
        onResolutionsImported();
      }
      
      setTimeout(() => setImportResult(null), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      {userRole === 'gestionnaire' && (
        <>
          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={onNewResolution}
                className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                data-testid="new-resolution-btn"
              >
                <Plus size={18} />
                <span>Nouvelle Résolution</span>
              </button>
              
              {/* Delete all button - only show if there are resolutions */}
              {selectedAG.resolutions && selectedAG.resolutions.length > 0 && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="border border-red-300 text-red-600 hover:bg-red-50 py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  data-testid="delete-all-resolutions-btn"
                >
                  <Trash2 size={18} />
                  <span>Effacer tout</span>
                </button>
              )}
            </div>
            
            {/* Info box about voting */}
            <div className="text-sm text-gray-500 flex items-center flex-wrap gap-2">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-emerald-500 rounded-full"></span>
                <span>Art. 24</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                <span>Art. 25</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                <span>Art. 26</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-purple-500 rounded-full"></span>
                <span>Unanimité</span>
              </span>
            </div>
          </div>

          {/* Import zone */}
          <div
            className={`border-2 border-dashed rounded-xl p-4 transition-all ${
              dragActive 
                ? 'border-[#00D3A8] bg-[#00D3A8]/5' 
                : 'border-gray-200 hover:border-gray-300'
            } ${importing ? 'pointer-events-none opacity-60' : 'cursor-pointer'}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => !importing && document.getElementById('resolution-import').click()}
          >
            <input
              id="resolution-import"
              type="file"
              accept=".docx,.csv"
              className="hidden"
              onChange={handleFileInput}
            />
            
            {importing ? (
              <div className="flex items-center justify-center space-x-3 py-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#00D3A8]"></div>
                <span className="text-gray-600">Analyse et import en cours...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-4">
                <div className="flex items-center space-x-2 text-gray-500">
                  <FileText size={20} className="text-blue-500" />
                  <span className="text-sm">Word (.docx)</span>
                </div>
                <span className="text-gray-300">|</span>
                <div className="flex items-center space-x-2 text-gray-500">
                  <FileSpreadsheet size={20} className="text-green-500" />
                  <span className="text-sm">CSV (.csv)</span>
                </div>
                <span className="text-gray-300">|</span>
                <div className="flex items-center space-x-2">
                  <Upload size={18} className={dragActive ? 'text-[#00D3A8]' : 'text-gray-400'} />
                  <span className="text-sm text-gray-600">
                    {dragActive ? 'Déposez le fichier' : 'Glissez ou cliquez pour importer'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Import result message */}
          {importResult && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center space-x-2">
              <Check size={18} className="text-green-600" />
              <span className="text-green-700 text-sm">{importResult.message}</span>
              <button onClick={() => setImportResult(null)} className="ml-auto text-green-500 hover:text-green-700">
                <X size={16} />
              </button>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
              <AlertCircle size={18} className="text-red-600" />
              <span className="text-red-700 text-sm">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
                <X size={16} />
              </button>
            </div>
          )}

          {/* Import help text */}
          <details className="text-sm text-gray-500">
            <summary className="cursor-pointer hover:text-gray-700">
              💡 Format attendu pour l'import
            </summary>
            <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-2">
              <p><strong>CSV :</strong> Colonnes : titre, description, type_majorite, clef_repartition</p>
              <p><strong>Word :</strong> Chaque résolution commence par un numéro ou "Résolution X :"</p>
              <p>Exemple Word :</p>
              <pre className="bg-white p-2 rounded text-xs mt-1 overflow-x-auto">
{`1. Approbation des comptes 2024
   Description détaillée de la résolution...

2. Vote du budget prévisionnel 2025
   Description...`}
              </pre>
            </div>
          </details>
        </>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Effacer toutes les résolutions ?</h3>
                <p className="text-sm text-gray-500">
                  {selectedAG.resolutions?.length || 0} résolution(s) seront supprimées
                </p>
              </div>
            </div>
            
            <p className="text-gray-600 mb-6">
              Cette action est irréversible. Toutes les résolutions de cette assemblée seront définitivement supprimées. 
              Les votes associés seront également effacés.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 py-2.5 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAllResolutions}
                disabled={deleting}
                className="flex-1 py-2.5 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {deleting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : (
                  <>
                    <Trash2 size={18} />
                    <span>Effacer tout</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voting panels for each resolution */}
      {selectedAG.resolutions && selectedAG.resolutions.length > 0 ? (
        <div className="space-y-4">
          {selectedAG.resolutions.map((resolution) => (
            <VotingPanel
              key={resolution.id}
              resolution={resolution}
              token={token}
              userRole={userRole}
              onVoteComplete={onVoteComplete}
              agStatus={selectedAG.status}
              hasConfirmedParticipation={hasConfirmedParticipation}
              participationMode={participationMode}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-gray-400 text-4xl mb-3">📋</div>
          <p className="text-gray-500">Aucune résolution pour cette assemblée</p>
          {userRole === 'gestionnaire' && (
            <button
              onClick={onNewResolution}
              className="mt-4 text-[#00D3A8] hover:text-[#00C7A1] font-medium"
            >
              + Ajouter une résolution
            </button>
          )}
        </div>
      )}

      {/* Summary table (collapsed by default) */}
      {selectedAG.resolutions && selectedAG.resolutions.length > 0 && (
        <details className="mt-6">
          <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800 font-medium">
            📊 Voir le tableau récapitulatif
          </summary>
          <div className="overflow-x-auto mt-3">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">N°</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titre</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Majorité</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Résultat</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {selectedAG.resolutions.map((resolution, index) => (
                  <tr key={resolution.id}>
                    <td className="px-4 py-3 font-medium">{resolution.ordre || index + 1}</td>
                    <td className="px-4 py-3">{resolution.titre}</td>
                    <td className="px-4 py-3 text-sm">{resolution.type_majorite}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-sm rounded ${
                        resolution.resultat === 'Adoptée' ? 'bg-green-100 text-green-700' :
                        resolution.resultat === 'Rejetée' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {resolution.resultat}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
};

export default AGResolutionsTab;
